import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { maxPlatform } from "../services/maxPlatform";
import en from "../../locales/en.json";
import ru from "../../locales/ru.json";

export const LOCALE_STORAGE_KEY = "mvk.locale";
/** Set when the user picks RU/EN in the language switcher (not auto-detect). */
export const LOCALE_USER_SET_KEY = "mvk.locale.user";
export type AppLocale = "ru" | "en";

export function normalizeLocale(raw: string | null | undefined): AppLocale {
  if (!raw) return "ru";
  const code = raw.trim().toLowerCase().split(/[-_]/)[0];
  return code === "en" ? "en" : "ru";
}

/** Auto: stored user choice → ru. Ignore MAX/browser EN until user picks EN. */
export function detectLocale(): AppLocale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    const userSet = localStorage.getItem(LOCALE_USER_SET_KEY) === "1";
    if (stored === "ru" || stored === "en") {
      // Pre-IDEA-039: MAX language_code=en could persist without an explicit pick.
      if (stored === "en" && !userSet && maxPlatform.isAvailable()) {
        localStorage.removeItem(LOCALE_STORAGE_KEY);
        return "ru";
      }
      return stored;
    }
  } catch {
    /* ignore */
  }
  // IDEA-039: service UI defaults to Russian (MAX language_code often en).
  return "ru";
}

export function getStoredLocaleOverride(): AppLocale | null {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored === "ru" || stored === "en") return stored;
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Switch UI + content locale. Must finish i18n + cache clear BEFORE notifying
 * listeners — otherwise remounted pages refetch with the old lang / stale RU pack.
 */
export async function setLocale(locale: AppLocale, persist = true): Promise<void> {
  if (persist) {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      localStorage.setItem(LOCALE_USER_SET_KEY, "1");
    } catch {
      /* ignore */
    }
  }
  await i18n.changeLanguage(locale);
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
  const [{ eventsService }, { eventPackService }, mapMod] = await Promise.all([
    import("../services/eventsService"),
    import("../services/eventPackService"),
    import("../services/mapService").catch(() => null),
  ]);
  eventsService.clearCache();
  eventPackService.clearCache();
  mapMod?.mapService.invalidate();
  try {
    const { uiStringsApi } = await import("../api/uiStrings");
    await uiStringsApi.applyToI18n(i18n, locale);
  } catch {
    /* optional CMS strings */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("mvk:locale-changed", { detail: locale }));
  }
}

export function intlLocale(locale: string = i18n.language): string {
  return normalizeLocale(locale) === "en" ? "en-US" : "ru-RU";
}

export function formatDateRange(start: string, end: string, locale?: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const loc = intlLocale(locale);
  const sameMonth =
    s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth();
  if (sameMonth) {
    const month = new Intl.DateTimeFormat(loc, { month: "short" }).format(s);
    return `${s.getDate()}–${e.getDate()} ${month} ${s.getFullYear()}`;
  }
  const fmt = new Intl.DateTimeFormat(loc, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${fmt.format(s)} – ${fmt.format(e)}`;
}

if (!i18n.isInitialized) {
  void i18n.use(initReactI18next).init({
    resources: {
      ru: { translation: ru },
      en: { translation: en },
    },
    lng: detectLocale(),
    fallbackLng: "ru",
    interpolation: { escapeValue: false },
  }).then(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = normalizeLocale(i18n.language);
    }
    void import("../api/uiStrings").then(({ uiStringsApi }) => {
      void uiStringsApi.applyToI18n(i18n, normalizeLocale(i18n.language));
    });
  });
} else if (typeof document !== "undefined") {
  document.documentElement.lang = normalizeLocale(i18n.language);
}

export default i18n;
